// frontend/src/components/aria/AriaPanel.js
import { useState, useEffect, useRef } from 'react';
import { X, ArrowRight, Trash } from '@phosphor-icons/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAriaContext } from '../../contexts/AriaContext';
import AriaActionCard from './AriaActionCard';
import api from '../../api';
import { readConversation, writeConversation, clearConversation } from '../../lib/workflowState';

const MONO = { fontFamily: "'JetBrains Mono', monospace" };

const S = {
  overlay: (open) => ({
    position: 'fixed', top: 0, right: 0, bottom: 0,
    width: open ? 340 : 0, zIndex: 800,
    background: 'hsl(0,0%,4%)',
    borderLeft: open ? '1px solid rgba(117,255,0,0.13)' : 'none',
    display: 'flex', flexDirection: 'column',
    transition: 'width 0.2s ease',
    overflow: 'hidden',
  }),
  header: {
    padding: '10px 14px', borderBottom: '1px solid hsl(0,0%,10%)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    flexShrink: 0,
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 8 },
  dot: { width: 7, height: 7, background: 'hsl(75,100%,50%)', borderRadius: '50%' },
  title: { ...MONO, fontSize: 10, color: 'hsl(75,100%,50%)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.12em' },
  ctx: { ...MONO, fontSize: 8, color: 'hsl(0,0%,30%)', textTransform: 'uppercase', letterSpacing: '.08em' },
  messages: { flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 12 },
  msgWrap: (role) => ({
    display: 'flex', flexDirection: 'column',
    alignItems: role === 'user' ? 'flex-end' : 'flex-start', gap: 3,
  }),
  roleLabel: { ...MONO, fontSize: 8, color: 'hsl(0,0%,30%)', textTransform: 'uppercase', letterSpacing: '.08em' },
  bubble: (role) => ({
    ...MONO, fontSize: 10, lineHeight: 1.6, padding: '8px 10px',
    background: role === 'user' ? 'hsl(75,100%,5%)' : 'hsl(0,0%,7%)',
    border: `1px solid ${role === 'user' ? 'hsl(75,100%,15%)' : 'hsl(0,0%,12%)'}`,
    color: 'hsl(0,0%,80%)', maxWidth: '92%',
  }),
  md: {
    fontSize: 10, lineHeight: 1.6, color: 'hsl(0,0%,80%)',
    fontFamily: "'JetBrains Mono', monospace",
  },
  input: {
    width: '100%', background: 'hsl(0,0%,7%)', border: '1px solid hsl(0,0%,15%)',
    padding: '10px 48px 10px 12px', color: 'hsl(0,0%,85%)', ...MONO, fontSize: 10,
    outline: 'none', resize: 'none', boxSizing: 'border-box', display: 'block',
  },
  footer: { padding: '10px 14px', borderTop: '1px solid hsl(0,0%,8%)', flexShrink: 0 },
  inputWrap: { position: 'relative' },
  sendBtn: (active) => ({
    position: 'absolute', bottom: 6, right: 6,
    background: active ? 'hsl(75,100%,50%)' : 'hsl(0,0%,12%)',
    border: 'none', color: active ? '#000' : 'hsl(0,0%,30%)',
    width: 30, height: 30, cursor: active ? 'pointer' : 'default',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }),
  hint: { ...MONO, fontSize: 8, color: 'hsl(0,0%,20%)', textAlign: 'center', marginTop: 6 },
  iconBtn: { background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(0,0%,35%)', display: 'flex', padding: 2 },
};

export default function AriaPanel() {
  const { isOpen, closePanel, pageContext, aiConfirm, user } = useAriaContext();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (user?.id && isOpen) {
      setMessages(prev => {
        if (prev.length > 0) return prev; // already have in-memory messages, don't overwrite
        const saved = readConversation(user.id);
        return saved.length > 0 ? saved : prev;
      });
    }
  }, [user?.id, isOpen]);

  useEffect(() => {
    if (user?.id && messages.length > 0) {
      writeConversation(user.id, messages);
    }
  }, [messages, user?.id]);

  useEffect(() => {
    if (bottomRef.current?.scrollIntoView) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const userMsg = { role: 'user', content: text };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setInput('');
    setLoading(true);
    try {
      const { data } = await api.post('/aria/chat', {
        messages: nextMessages,
        page_context: pageContext,
      });
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.reply,
        actions: data.actions || [],
      }]);
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant', content: 'Sorry, I ran into an error. Please try again.', actions: [],
      }]);
    }
    setLoading(false);
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') closePanel(); };
    if (isOpen) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, closePanel]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const handleClearChat = () => {
    setMessages([]);
    if (user?.id) clearConversation(user.id);
  };

  if (!isOpen) return null;

  return (
    <div data-testid="aria-panel" style={S.overlay(isOpen)}>
      <div style={S.header}>
        <div style={S.headerLeft}>
          <div style={S.dot} />
          <span style={S.title}>ARIA</span>
          <span style={S.ctx}>· {aiConfirm ? pageContext.page : 'autonomous · ' + pageContext.page}</span>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button style={S.iconBtn} onClick={handleClearChat} title="Clear chat" data-testid="aria-clear-chat">
            <Trash size={13} />
          </button>
          <button style={S.iconBtn} onClick={closePanel} title="Close" data-testid="aria-close-panel">
            <X size={13} />
          </button>
        </div>
      </div>
      <div style={S.messages}>
        {messages.length === 0 && (
          <div style={{ ...MONO, fontSize: 10, color: 'hsl(0,0%,30%)', textAlign: 'center', marginTop: 40 }}>
            Ask me anything or give a command.
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} style={S.msgWrap(msg.role)}>
            <span style={S.roleLabel}>{msg.role === 'user' ? 'You' : 'ARIA'}</span>
            <div style={S.bubble(msg.role)}>
              {msg.role === 'assistant' ? (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    p: ({children}) => <p style={{margin: '0 0 6px 0'}}>{children}</p>,
                    table: ({children}) => <table style={{borderCollapse:'collapse',width:'100%',marginBottom:6}}>{children}</table>,
                    th: ({children}) => <th style={{border:'1px solid hsl(0,0%,20%)',padding:'3px 6px',textAlign:'left',background:'hsl(0,0%,10%)'}}>{children}</th>,
                    td: ({children}) => <td style={{border:'1px solid hsl(0,0%,15%)',padding:'3px 6px'}}>{children}</td>,
                    strong: ({children}) => <strong style={{color:'hsl(75,100%,60%)'}}>{children}</strong>,
                    code: ({children}) => <code style={{background:'hsl(0,0%,10%)',padding:'1px 4px'}}>{children}</code>,
                    ul: ({children}) => <ul style={{margin:'0 0 6px 0',paddingLeft:16}}>{children}</ul>,
                    ol: ({children}) => <ol style={{margin:'0 0 6px 0',paddingLeft:16}}>{children}</ol>,
                    li: ({children}) => <li style={{marginBottom:2}}>{children}</li>,
                  }}
                >
                  {msg.content}
                </ReactMarkdown>
              ) : msg.content}
            </div>
            {msg.role === 'assistant' && msg.actions?.length > 0 && (
              <div style={{ width: '100%' }}>
                {msg.actions.map((action, ai) => (
                  <AriaActionCard
                    key={ai}
                    action={action}
                    index={ai + 1}
                    total={msg.actions.length}
                    requireConfirm={aiConfirm}
                    onSkip={() => {}}
                    onConfirm={() => {}}
                  />
                ))}
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div style={{ ...MONO, fontSize: 9, color: 'hsl(75,100%,40%)' }}>ARIA is thinking...</div>
        )}
        <div ref={bottomRef} />
      </div>
      <div style={S.footer}>
        <div style={S.inputWrap}>
          <textarea
            style={S.input}
            rows={2}
            placeholder="Ask anything or give a command..."
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            data-testid="aria-input"
          />
          <button style={S.sendBtn(!!input.trim())} onClick={sendMessage} data-testid="aria-send">
            <ArrowRight size={14} weight="bold" />
          </button>
        </div>
        <div style={S.hint}>Enter to send · Shift+Enter for new line</div>
      </div>
    </div>
  );
}
